import React, { useState, useEffect, useCallback } from "react";
import { Grid, CardHeader, CardContent, Box, TextField, FormControlLabel, Switch } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../../layouts/CardLayout";
import errorHandler from "../../../utils/errorHandler";
import { locationServices } from "../../../services/AllServices";

function AddEditCountry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await locationServices.getById({ countryId:id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: data?.name || "",
      code: data?.code || "",
      status: data?.status ?? true,
    },
    validate: values => {
      const errors = {};
      if (!values.name.trim()) {
        errors.name = "This field is required";
      }
      if (!values.code.trim()) {
        errors.code = "This field is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          countryId: id || undefined,
          name: values.name.trim(),
          code: values.code.trim(),
          status: values.status,
        };

        const response = id
          ? await locationServices.edit(payload)
          : await locationServices.add(payload);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = formik;

  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Country`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Country Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Country Code"
                    required
                    name="code"
                    value={values.code}
                    onChange={handleChange}
                    error={Boolean(touched.code && errors.code)}
                    helperText={touched.code && errors.code}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.status}
                        onChange={(e) => setFieldValue("status", e.target.checked)}
                        name="status"
                        color="primary"
                      />
                    }
                    label="Status (Active)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Country
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditCountry;